import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Award, Wrench, Clock, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

const Datenschutz = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Datenschutz Hauptbereich */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg">
            Datenschutzerklärung – Jannesmann Baumaschinen
          </h1>

          {/* Textbereich */}
          <div className="bg-gray-50 rounded-b-xl border border-gray-200 border-t-0 p-6 md:p-8">

            {/* 1. Datenschutz auf einen Blick */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                1. Datenschutz auf einen Blick
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>
            </div>

            {/* 2. Verantwortliche Stelle */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                2. Verantwortliche Stelle
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Jannesmann Handels und Import GbR<br />
                Gesellschafter: Yasar Kartal, Jan Hassel und Ayten Celik<br />
                Poststraße 6<br />
                46236 Bottrop<br />
                Deutschland<br /><br />
                E-Mail: info@jannesmann.de
              </p>
            </div>

            {/* 3. Datenerfassung auf dieser Website */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                3. Datenerfassung auf dieser Website
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                <strong>Kontaktformular:</strong> Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                <strong>Server-Log-Dateien:</strong> Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind: Browsertyp und Browserversion, verwendetes Betriebssystem, Referrer URL, Hostname des zugreifenden Rechners, Uhrzeit der Serveranfrage, IP-Adresse.
              </p>
            </div>

            {/* 4. Ihre Rechte */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                4. Ihre Rechte
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>Recht auf Auskunft (Art. 15 DSGVO)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>Recht auf Berichtigung (Art. 16 DSGVO)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>Recht auf Löschung (Art. 17 DSGVO)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>Widerspruchsrecht (Art. 21 DSGVO)</span>
                </li>
              </ul>
            </div>

            {/* 5. Cookies */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                5. Cookies
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Unsere Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Webbrowser auf Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen. Einige Cookies sind „Session-Cookies", die nach Ende Ihrer Browser-Sitzung von selbst gelöscht werden. Andere Cookies verbleiben auf Ihrem Endgerät, bis Sie diese löschen. Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden.
              </p>
            </div>

            {/* 6. SSL-/TLS-Verschlüsselung */}
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                6. SSL-/TLS-Verschlüsselung
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine SSL-/TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = { Award, Wrench, Clock, HeadphonesIcon: Phone }[feature.icon];
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feat, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">{t('cta.contactUs')}</p>
                  <Link to="/kontakt">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      {t('cta.getInTouch')}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Zwei Videos */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Datenschutz;
